import React from 'react';
import { useQueryClient } from 'react-query';
import { Form as FinalForm, Field } from 'react-final-form';
import {
    Form, Button, Select, Radio, Header
} from 'semantic-ui-react';
import Dimmer from '+/Dimmer';
import History from '#/history';
import { scopeValidator } from '#/permissions';
import { getState } from '../../store';
import { useDelinquencies, useUpdateDelinquency } from '~/entrataReports';

// eslint-disable-next-line max-lines-per-function
const DelinquencyReportForm = props => {

    const { match } = props;
    const { property_id, id } = match.params;

    const queryClient = useQueryClient();
    const { data: delinquencies, isLoading } = useDelinquencies(property_id);
    const { mutate: updateReportData } = useUpdateDelinquency({
        onSuccess: () => {
            queryClient.invalidateQueries('delinquencies');
            setTimeout(() => {
                History.push(`/properties/${property_id}/reports/delinquencies`);
            }, 1000);
        }
    });
    const { auth } = getState();

    const report = (delinquencies || []).find(report => report.id === id);
    const adminOrLegal = scopeValidator(auth.role, ['legal', 'legal_admin']);

    const onSubmit = ({ id, ...values }) => {
        updateReportData({
            id,
            property_id,
            data: values
        });
    };

    if (isLoading || !delinquencies) return <Dimmer />;

    return (
        <div style={styles.root}>
            <FinalForm
                initialValues={{
                    id: report.id,
                    ...report.data
                }}
                onSubmit={onSubmit}
                mutators={{
                    addPayment: (args, state, { changeValue }) => {
                        changeValue(state, 'stip_payments', values => [
                            ...(values || []),
                            {}
                        ]);
                    },
                    removePayment: ([paymentIndex], state, { changeValue }) => {
                        changeValue(state, 'stip_payments', values => {
                            const clone = [...values];
                            clone.splice(paymentIndex, 1);

                            return clone;
                        });
                    }
                }}
                // eslint-disable-next-line max-lines-per-function
                render={({
                    handleSubmit,
                    submitting,
                    pristine,
                    values,
                    form
                }) => (
                    <Form onSubmit={handleSubmit}>
                        <Header as='h2'>{report.property_name}</Header>
                        <Header.Subheader style={{ marginBottom: 40 }}>
                            {report.address && report.address.join(', ')}
                        </Header.Subheader>
                        <div style={styles.formBox}>
                            <Header as='h2' style={{ marginBottom: 25 }}>
								Unit Information
                            </Header>
                            <Form.Group widths='equal'>
                                <Form.Input
                                    label='Unit #'
                                    value={report.bldg_unit || ''}
                                    disabled
                                />
                                <Form.Input
                                    label='Resident Name'
                                    value={report.resident_name || ''}
                                    disabled
                                />
                                <Form.Input
                                    label='Lease Status'
                                    value={report.lease_status || ''}
                                    disabled
                                />
                            </Form.Group>
                            <Form.Group widths='equal'>
                                <Form.Input
                                    label='Total Current Due'
                                    value={report.total_current_due || ''}
                                    disabled
                                />
                                <Form.Input
                                    label='Total Past Due'
                                    value={report.total_past_due || ''}
                                    disabled
                                />
                                <Form.Input
                                    label='Total Delinquent'
                                    value={report.total_delinquent || ''}
                                    disabled
                                />
                            </Form.Group>
                            <Form.Group widths='equal'>
                                <Form.Input
                                    label='Payment Status'
                                    value={report.payment_status || ''}
                                    disabled
                                />
                                <Form.Input
                                    label='Lease Sub Status'
                                    value={report.lease_sub_status || ''}
                                    disabled
                                />
                                <Form.Input
                                    label='Scheduled Rent'
                                    value={report.scheduled_rent || ''}
                                    disabled
                                />
                                <Form.Input
                                    label='Other Scheduled Rent'
                                    value={report.other_scheduled_rent || ''}
                                    disabled
                                />
                            </Form.Group>
                        </div>

                        <div style={styles.formBox}>
                            <Header as='h2' style={{ marginBottom: 25 }}>
								Manager Information
                            </Header>
                            <Form.Group widths='equal'>
                                <Field
                                    component={Form.Input}
                                    label='Manager Notes'
                                    name={'manager_notes'}
                                />
                                <Field
                                    component={Form.Input}
                                    label='Hold Date'
                                    name={'hold_date'}
                                    type='date'
                                />
                                <Field
                                    component={Form.Input}
                                    label='Held By'
                                    name={'held_by'}
                                />
                            </Form.Group>
                        </div>

                        {adminOrLegal && (
                            <>
                                <div style={styles.formBox}>
                                    <Header
                                        as='h2'
                                        style={{ marginBottom: 25 }}
                                    >
										Leasee Information
                                    </Header>
                                    <Form.Group widths='equal'>
                                        <Field
                                            component={Form.Input}
                                            label='Second Leasee'
                                            name={'second_leasee'}
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='Third Leasee'
                                            name={'third_leasee'}
                                        />
                                    </Form.Group>

                                    <Form.Group widths='equal'>
                                        <Field
                                            component={Form.Input}
                                            label='Admin Notes'
                                            name={'admin_notes'}
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='General Notes'
                                            name={'general_notes'}
                                        />
                                    </Form.Group>
                                    <Form.Group widths='equal'>
                                        <Field
                                            component={Form.Input}
                                            label='Court Registry'
                                            name={'court_registry'}
                                            type='number'
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='Case #'
                                            name={'case_number'}
                                        />
                                    </Form.Group>
                                    <Form.Group widths='equal'>
                                        <Field
                                            component={Form.Input}
                                            label='3 Day Served'
                                            name={'three_day_served'}
                                            type='date'
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='3 Day Expiration Date'
                                            name={'three_day_expiration_date'}
                                            type='date'
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='File Date'
                                            name={'file_date'}
                                            type='date'
                                        />
                                    </Form.Group>
                                    <Form.Group widths='equal'>
                                        <Field
                                            component={Form.Input}
                                            label='Summons Served'
                                            name={'summons_served'}
                                            type='date'
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='Default Date'
                                            name={'default_date'}
                                            type='date'
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='Motion To Strike'
                                            name={'motion_to_strike'}
                                            type='date'
                                        />
                                    </Form.Group>
                                    <Form.Group widths='equal'>
                                        <Field
                                            component={Form.Input}
                                            label='Judgement'
                                            name={'judgement'}
                                            type='date'
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='Writ of Possession'
                                            name={'writ_of_possession'}
                                            type='date'
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='24 Hour Notice'
                                            name={'twentyfour_hour_notice'}
                                            type='date'
                                        />
                                    </Form.Group>
                                </div>

                                <div style={styles.formBox}>
                                    <Header
                                        as='h2'
                                        style={{ marginBottom: 25 }}
                                    >
										Stipulation Information
                                    </Header>

                                    <Form.Group widths='equal'>
                                        <Field
                                            component={Form.Input}
                                            label='Initial Payment'
                                            name={'initial_payment'}
                                            type='number'
                                            step='0.01'
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='Balance'
                                            name={'balance'}
                                            type='number'
                                            step='0.01'
                                        />
                                        <Field
                                            component={Form.Input}
                                            label='Stipulation Signed Date'
                                            name={'stipulation_signed_date'}
                                            type='date'
                                        />
                                    </Form.Group>
                                    <div style={{ marginBottom: '10px' }}>
                                        <Button
                                            onClick={e => {
                                                e.preventDefault();
                                                form.mutators.addPayment();
                                            }}
                                        >
											Add Payment Info
                                        </Button>
                                    </div>
                                    {(values.stip_payments || []).map(
                                        (payments, i) => (
                                            <Form.Group widths='equal' key={i}>
                                                <Field
                                                    component={Form.Input}
                                                    label={`Payment ${i + 1}`}
                                                    name={`stip_payments[${i}].amount`}
                                                />
                                                <Field
                                                    component={Form.Input}
                                                    label={`Payment ${
														i + 1
													} Date`}
                                                    name={`stip_payments[${i}].date`}
                                                    type='date'
                                                />
                                                <Form.Button
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        form.mutators.removePayment(
                                                            i
                                                        );
                                                    }}
                                                >
													X
                                                </Form.Button>
                                            </Form.Group>
                                        )
                                    )}
                                </div>
                            </>
                        )}
                        <div style={styles.formBox}>
                            <Header as='h2' style={{ marginBottom: 25 }}>
								Special Information
                            </Header>
                            <Form.Group widths='equal'>
                                <Field name='special_status'>
                                    {({ input }) => (
                                        <Form.Field width='5'>
                                            <label htmlFor='special_status'>
												Special Status
                                            </label>
                                            <Select
                                                clearable
                                                value={input.value}
                                                onChange={(e, t) => input.onChange(t.value)
                                                }
                                                placeholder='Special Status'
                                                options={specialStatusOptions}
                                            />
                                        </Form.Field>
                                    )}
                                </Field>

                                <Field name='flagged'>
                                    {({ input, meta }) => (
                                        <Form.Field>
                                            <label htmlFor='flagged'>
												Flagged
                                            </label>
                                            <Radio
                                                toggle
                                                checked={!!input.value}
                                                onChange={(_, { checked }) => input.onChange(checked)
                                                }
                                            />
                                        </Form.Field>
                                    )}
                                </Field>
                            </Form.Group>
                        </div>
                        <div style={styles.formBoxBottom}>
                            <Button
                                style={styles.formButton}
                                onClick={() => props.history.push(
										`/properties/${property_id}/reports/delinquencies`
                                )
                                }
                            >
								Cancel
                            </Button>
                            <Button
                                style={styles.formButton}
                                positive
                                type='submit'
                                disabled={submitting || pristine}
                            >
								Submit
                            </Button>
                        </div>
                    </Form>
                )}
            />
        </div>
    );
};

const specialStatusOptions = [
    {
        key: 'nsf',
        value: 'NSF',
        text: 'NSF'
    },
    {
        key: 's8',
        value: 'Section 8',
        text: 'Section 8'
    },
    {
        key: 'adj',
        value: 'Adjustment',
        text: 'Adjustment'
    },
    // {
    //     key: 'ra',
    //     value: 'Repayment Agreement',
    //     text: 'Repayment Agreement'
    // },
    {
        key: 'emp',
        value: 'Employee',
        text: 'Employee'
    },
    {
        key: 'ga',
        value: 'Govt. Assistance',
        text: 'Govt. Assistance'
    },
    {
        key: 'ex',
        value: 'Exception',
        text: 'Exception'
    }
];

const styles = {
    root: { padding: '20px' },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        margin: '10px',
        alignItems: 'center',
        paddingTop: '20px'
    },
    formBox: {
        margin: '20px 0px',
        boxShadow: '0px 0px 2px 0px #ccc',
        padding: 25,
        borderRadius: 8
    },
    formBoxBottom: {
        margin: '20px 0px',
        padding: '25px 0px',
        display: 'flex',
        justifyContent: 'flex-end'
    },
    formButton: {
        width: '250px',
        height: '45px'
    }
};

export default DelinquencyReportForm;
